import { BaseElementDef, BaseElementDefBase } from "./BaseElementDef";
import { BehaviorSubject, combineLatest, from, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { stringCoalesce } from "@/lib/whitespaceMethods";
import { AddressSettings } from "@cs-ts/address-settings";
import { FormDefinitionItem } from "./__DefinitionDataInterface";
import { CalculationContext } from "./__CalculationContextInterface";
import { FormItem } from "@/cs-ts-bridge/cs/FormItem";
import { AddressVerificationEnum } from "@cs-ts/address-verification-enum";
import { InvokeBody } from "@cs-ts/invoke-body";
import { usePageStore } from "../pageState/store";
import { useMainStore } from "@/mainStore";
import { KnownFeatureFlags } from "@cs/KnownFeatureFlags";
export type VerifiedAddress = { Address1: string | null; Address2: string | null; City: string | null; State: string | null; ZipCode: string | null; Country: string | null; Messages: string[] | null; Latitude: number | null; Longitude: number | null; VerificationStatus: string | null; AddressPrecision: string | null; Changes: Changes | null; ErrorMessages: string[]; Code: string };
export type Changes = { Address1Number: string | null; Address1Street: string | null; City: string | null; State: string | null; ZipCode: string | null };
export type AddressVerificationResponse = {
    Success: boolean;
    Messages: string;
    AddressChanged: boolean;
    Result: VerifiedAddress;
};
export interface FormAddressVerificationItem extends FormItem {
    verificationResponse: string;
    currentAddressData: string;
    verified: boolean | null;
}
export default class AddressDef extends BaseElementDef<AddressSettings, FormDefinitionItem<AddressSettings>, FormItem> {
    declare _effectiveLabel$: BehaviorSubject<string | null | undefined>;
    declare validationMessages: string[];
    declare verified: boolean;
    declare verified$: BehaviorSubject<boolean>;
    declare verificationResponse: string;
    declare currentAddressData: string;
    declare errorMessages$: BehaviorSubject<string>;
    constructor(parentElement: BaseElementDefBase | null, elementData: FormItem, definitionData: FormDefinitionItem<AddressSettings>, calculationContext: CalculationContext) {
        super(parentElement, elementData, definitionData, calculationContext);
        this.typeName = "MI.Common.DynamicForms.Models.DFAddress"; // TODO - not hardcoded?
        this.errorMessages$ = new BehaviorSubject("");
    }

    effectiveLabel$() {
        if (this._effectiveLabel$ == null) {
            if (this.definition.settings.labelCalculationId) {
                this._effectiveLabel$ = new BehaviorSubject(null as string | null | undefined);
                this.resolveCalculation(this.definition.settings.labelCalculationId)
                    .pipe(map(i => (i.length ? i[0] : null)))
                    .subscribe(this._effectiveLabel$);
            } else {
                this._effectiveLabel$ = new BehaviorSubject(this.definition.settings.label as string | null | undefined);
            }
        }
        return this._effectiveLabel$;
    }

    effectiveShortLabel$() {
        if (this.definition.settings.shortLabel) {
            return from([this.definition.settings.shortLabel]);
        } else {
            return this.effectiveLabel$();
        }
    }

    setData(data: FormAddressVerificationItem, excludeChildren: boolean) {
        super.setData(data, excludeChildren);
        this.setKey(data, "validationMessages");
        this.setKey(data, "verified");
        this.setKey(data, "verificationResponse");
        this.setKey(data, "currentAddressData");
    }

    getData(excludeChildren: boolean) {
        const result = super.getData(excludeChildren) as FormAddressVerificationItem;
        if (result != null) {
            if (this.currentAddressData != null) {
                result.currentAddressData = this.currentAddressData;
            }
        }
        return result;
    }

    async verifyAddress(address: string, abortController: AbortController | null) {
        const pageStore = usePageStore();
        const mainStore = useMainStore();
        const eliminateDuplicateSubmissions = await mainStore.isFeatureFlagActiveAsync(KnownFeatureFlags.EliminateDuplicateSubmission.Id);
        const adjuster = (postData: InvokeBody) => {
            postData.noWrite = false;
            if (eliminateDuplicateSubmissions && pageStore.formId == null) {
                postData.skipForm = false;
                postData.setUser = true;
            }
            return postData;
        };
        let response: AddressVerificationResponse;
        if (eliminateDuplicateSubmissions) {
            const rawResponse = await this.invokeRawAsync("verifyAddress", address, adjuster, abortController);
            if (rawResponse?.Form != null) {
                pageStore._setFormId(rawResponse?.Form.formId);
            }
            response = JSON.parse(rawResponse.Result) as AddressVerificationResponse;
        } else {
            response = (await this.invokeAsync("verifyAddress", address, adjuster, abortController)) as AddressVerificationResponse;
        }
        this.setVerified((this.definition.settings.verifyAddress == AddressVerificationEnum.Verify && (response.Result.Code == "NOT_FOUND" || response.Result.Code == null) && !response.Success) || response.Success);
        this.verificationResponse = response.Messages;
        this.errorMessages$.next(response.Messages ?? "");
        this.markEdited();
        return response;
    }

    setVerified(state: boolean) {
        this.verified = state;
        this.verified$.next(state);
    }

    effectiveLabelText$() {
        return combineLatest(this.effectiveShortLabel$() as Observable<string | null | undefined>, this.effectiveLabel$() as Observable<string | null | undefined>).pipe(map(([s, l]) => stringCoalesce(l, s, null)));
    }

    valid$() {
        return combineLatest([super.valid$(), this.verified$, this.edited$()]).pipe(map(([valid, verified, edited]) => (edited ? valid && (this.definition.settings.verifyAddress != AddressVerificationEnum.Off ? verified : true) : true)));
    }

    errors$() {
        return combineLatest([this.valid$(), this.errorMessages$]).pipe(map(([valid, errorMessages]) => (valid || this.definition.settings.verifyAddress == AddressVerificationEnum.Verify ? [] : errorMessages ? [errorMessages] : this.validationMessages.length > 0 ? this.validationMessages : [])));
    }
}
